export type {
  SfxId,
  MusicTrackId,
  Vec3,
  EmotePlayedEvent,
  ListenerTransformProvider,
  PlayerPositionProvider,
  AudioManager,
} from './types';

import type { SfxId, MusicTrackId, ListenerTransformProvider, AudioManager } from './types';
import type { AudioAssetLoader } from '../assets/audio-loader';
import { createSfxPool } from './sfx-pool';
import { createMusicStream } from './music-stream';
import { createVolumeController } from './volume';
import { createMusicDucker } from './duck';
import { createSpatialEmitter } from './spatial';
import { isMusicMuted, isSfxMuted } from '../audio-settings';

/**
 * Maps server-broadcast emoteId (1..6) to the corresponding SFX manifest ID.
 *
 * Ordering matches roadmap line 377: "GG, Oof, Taunt, Thanks, Cry, Cheer".
 * Wire type source: src/shared/protocol/messages.ts:59 (EmotePlayedEvent).
 *
 * Declared `as const` so the value type narrows to the literal SfxId per
 * task at each key rather than widening to `string`.
 */
export const EMOTE_SFX_MAP = {
  1: 'sfx-emote-gg',
  2: 'sfx-emote-oof',
  3: 'sfx-emote-taunt',
  4: 'sfx-emote-thanks',
  5: 'sfx-emote-cry',
  6: 'sfx-emote-cheer',
} as const satisfies Record<1 | 2 | 3 | 4 | 5 | 6, SfxId>;

/** All shipped SFX ids the manager preloads by default. */
export const KNOWN_SFX_IDS: readonly SfxId[] = [
  // Ambient loops
  'sfx-ambient-crickets-loop',
  'sfx-ambient-magic-hum-loop',
  'sfx-ambient-water-loop',
  'sfx-ambient-wind-loop',
  // Board
  'sfx-board-bridge-cross',
  'sfx-board-island-arrive',
  'sfx-board-portal-enter',
  'sfx-board-tile-land-bad',
  'sfx-board-tile-land-good',
  'sfx-board-treasure-open',
  // Combat
  'sfx-combat-block',
  'sfx-combat-hit-heavy',
  'sfx-combat-hit-light',
  // Celebration / countdown / crowd
  'sfx-confetti-pop',
  'sfx-countdown-1',
  'sfx-countdown-2',
  'sfx-countdown-3',
  'sfx-countdown-go',
  'sfx-crowd-aww-disappointed',
  'sfx-crowd-cheer-big',
  'sfx-crowd-cheer-small',
  'sfx-crowd-laugh-comic',
  // Dice / economy
  'sfx-dice-land',
  'sfx-dice-roll',
  'sfx-economy-coin-pickup',
  'sfx-economy-key-pickup',
  'sfx-economy-purchase',
  // Emotes
  'sfx-emote-gg',
  'sfx-emote-oof',
  'sfx-emote-taunt',
  'sfx-emote-thanks',
  'sfx-emote-cry',
  'sfx-emote-cheer',
  // Fanfares / items
  'sfx-fanfare-victory',
  'sfx-items-cursed-relic-pulse',
  'sfx-items-cursed-relic-tick',
  'sfx-items-dimension-rift-arrive',
  'sfx-items-dimension-rift-open',
  'sfx-items-frost-blaster-impact',
  'sfx-items-frost-blaster-shoot',
  'sfx-items-gravity-well-cast',
  'sfx-items-gravity-well-pull',
  'sfx-items-lightning-dash-zip',
  'sfx-items-meteor-strike-impact',
  'sfx-items-meteor-strike-whistle',
  'sfx-items-mystery-box-chime',
  'sfx-items-mystery-box-open',
  'sfx-items-orbital-cannon-charge',
  'sfx-items-orbital-cannon-discharge',
  'sfx-items-phoenix-potion-revive',
  'sfx-items-shadow-stalker-footsteps',
  'sfx-items-shadow-stalker-kill',
  'sfx-items-shadow-stalker-summon',
  'sfx-items-soul-duel-portal',
  'sfx-items-soul-duel-victory',
  'sfx-items-stone-skin-activate',
  'sfx-items-thunder-fist-cast',
  'sfx-items-thunder-fist-hit',
  'sfx-items-toxic-cloud-disperse',
  // Minigames / misc / movement
  'sfx-minigame-correct',
  'sfx-minigame-end-fanfare',
  'sfx-minigame-start-bell',
  'sfx-minigame-tick-tock-warning',
  'sfx-minigame-wrong',
  'sfx-misc-buzzer-negative',
  'sfx-misc-ding-positive',
  'sfx-movement-jump',
  'sfx-movement-land',
  'sfx-movement-step-soft',
  // Shadow stalker / stings / transitions / UI
  'sfx-stalker-banish-puff',
  'sfx-stalker-spawn-shriek',
  'sfx-stalker-walk-loop',
  'sfx-sting-defeat',
  'sfx-transitions-whoosh-in',
  'sfx-transitions-whoosh-out',
  'sfx-ui-button-click',
  'sfx-ui-button-hover',
  'sfx-ui-error',
  'sfx-ui-menu-close',
  'sfx-ui-menu-open',
] as const;

export interface AudioManagerDeps {
  /** Caller-owned AudioContext (created on first user gesture per autoplay policy). */
  readonly audioContext: AudioContext;
  /** Audio asset loader produced by Plan 9 (audio-loader.ts:8-16). */
  readonly loader: AudioAssetLoader;
  /** Resolves a manifest id to a same-origin URL. Caller wires this against
   *  the existing manifest (e.g. (id) => `/${manifest.byId.get(id)?.path}`). */
  readonly resolveUrl: (id: SfxId | MusicTrackId) => string;
  /** Plan 21a render loop's addFrameTickCallback (renderer/types.ts:114).
   *  AudioManager subscribes its own tick during init(); unsubscribe is held
   *  internally and called from dispose(). */
  readonly addFrameTickCallback: (cb: (dt: number) => void) => () => void;
  /** Returns the listener (camera) transform; null pre-scene. */
  readonly getListenerTransform: ListenerTransformProvider;
  /** Optional injection point for createMusicStream — defaults to `() => new Audio()`.
   *  Injected in tests to avoid DOM dependency in node environment. */
  readonly createAudioElement?: () => HTMLAudioElement;
  /** Optional storage override for createVolumeController — defaults to `globalThis.localStorage`.
   *  Injected in tests to avoid localStorage dependency in node environment. */
  readonly storage?: Pick<Storage, 'getItem' | 'setItem'>;
  /** Optional manifest-derived SFX list. Defaults to KNOWN_SFX_IDS. */
  readonly preloadSfxIds?: readonly SfxId[];
}

/**
 * Composes all audio sub-modules (sfx pool, music stream, volume controller,
 * music ducker, spatial emitter) behind the unified AudioManager interface.
 *
 * AudioManager assumes ownership of the AudioContext from `init()` onwards;
 * do NOT also close it externally.
 */
export function createAudioManager(deps: AudioManagerDeps): AudioManager {
  const {
    audioContext,
    loader,
    resolveUrl,
    addFrameTickCallback,
    getListenerTransform,
    createAudioElement,
    storage,
    preloadSfxIds = KNOWN_SFX_IDS,
  } = deps;

  let initialized = false;
  let disposed = false;
  let unsubscribeTick: (() => void) | null = null;

  // Sub-modules are created inside init() once the gain-node chain exists.
  // Typed as their interfaces; assigned during init().
  let sfxPool: ReturnType<typeof createSfxPool>;
  let musicStream: ReturnType<typeof createMusicStream>;
  let volumeController: ReturnType<typeof createVolumeController>;
  let musicDucker: ReturnType<typeof createMusicDucker>;
  let spatialEmitter: ReturnType<typeof createSpatialEmitter>;

  function tick(dt: number): void {
    spatialEmitter.updateListener();
    void dt; // dt reserved for future per-frame work
  }

  async function init(): Promise<void> {
    if (initialized) return;

    // 1. Construct GainNode chain: masterGain → destination, sfx/music → master
    const masterGain = audioContext.createGain();
    const sfxGain = audioContext.createGain();
    const musicGain = audioContext.createGain();
    masterGain.connect(audioContext.destination);
    sfxGain.connect(masterGain);
    musicGain.connect(masterGain);

    // 2. Construct sub-modules
    volumeController = createVolumeController({
      masterGain,
      sfxGain,
      musicGain,
      currentTime: () => audioContext.currentTime,
      ...(storage !== undefined ? { storage } : {}),
    });
    sfxPool = createSfxPool({
      audioContext,
      loader,
      resolveUrl: resolveUrl as (id: SfxId) => string,
      sfxGain,
    });
    musicStream = createMusicStream({
      audioContext,
      musicGain,
      resolveUrl: resolveUrl as (id: MusicTrackId) => string,
      ...(createAudioElement !== undefined ? { createAudioElement } : {}),
    });
    musicDucker = createMusicDucker({
      audioContext,
      musicGain,
      getUserMusicVolume: () => volumeController.get('music'),
    });
    spatialEmitter = createSpatialEmitter({ audioContext, sfxGain, getListenerTransform });

    // 3. Restore volumes from localStorage (sync)
    volumeController.restore();

    // 4. Preload all known SFX ids
    await sfxPool.preload(preloadSfxIds);

    // 5. Subscribe tick to the render loop
    unsubscribeTick = addFrameTickCallback(tick);

    initialized = true;
  }

  function playSfx(
    id: SfxId,
    opts?: { spatialPosition?: { x: number; y: number; z: number } },
  ): void {
    if (isSfxMuted()) return;
    if (opts?.spatialPosition != null) {
      const panner = spatialEmitter.createPannerForPosition(opts.spatialPosition);
      sfxPool.play(id, { destination: panner });
    } else {
      sfxPool.play(id);
    }
  }

  function playDiceRoll(opts?: { spatialPosition?: { x: number; y: number; z: number } }): void {
    playSfx('sfx-dice-roll', opts);
  }

  function playDiceImpact(opts?: { spatialPosition?: { x: number; y: number; z: number } }): void {
    playSfx('sfx-dice-land', opts);
  }

  function playMusic(id: MusicTrackId, opts?: { fadeMs?: number; loop?: boolean }): void {
    if (isMusicMuted()) return;
    musicStream.play(id, opts);
  }

  function stopMusic(opts?: { fadeMs?: number }): void {
    musicStream.stop(opts);
  }

  function setVolume(channel: 'master' | 'sfx' | 'music', value: number): void {
    volumeController.set(channel, value);
  }

  function getVolume(channel: 'master' | 'sfx' | 'music'): number {
    return volumeController.get(channel);
  }

  function duck(durationMs: number, depth?: number): void {
    musicDucker.duck(durationMs, depth);
  }

  function unduck(): void {
    musicDucker.unduck();
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    unsubscribeTick?.();
    musicStream.dispose();
    sfxPool.dispose();
    void audioContext.close();
  }

  return {
    init,
    playSfx,
    playDiceRoll,
    playDiceImpact,
    playMusic,
    stopMusic,
    setVolume,
    getVolume,
    duck,
    unduck,
    tick,
    dispose,
  };
}

export interface PlayToneTestOptions {
  readonly frequencyHz?: number;
  readonly durationMs?: number;
  readonly gain?: number;
}

/**
 * Minimal procedural audio smoke test. Intended for follow-up wiring from a
 * first-user-gesture handler so browser autoplay policy can unlock the context.
 */
export async function playToneTest(
  audioContext: AudioContext,
  opts: PlayToneTestOptions = {},
): Promise<void> {
  if (audioContext.state === 'closed') {
    throw new Error('playToneTest: AudioContext is closed');
  }

  if (audioContext.state === 'suspended') {
    await audioContext.resume();
  }

  const frequencyHz = opts.frequencyHz ?? 660;
  const durationSeconds = Math.max(0.03, (opts.durationMs ?? 120) / 1000);
  const gainValue = Math.min(1, Math.max(0, opts.gain ?? 0.08));
  const now = audioContext.currentTime;
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(frequencyHz, now);
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(gainValue, now + 0.01);
  gain.gain.linearRampToValueAtTime(0, now + durationSeconds);

  oscillator.connect(gain);
  gain.connect(audioContext.destination);

  await new Promise<void>((resolve) => {
    oscillator.onended = () => {
      oscillator.disconnect();
      gain.disconnect();
      resolve();
    };
    oscillator.start(now);
    oscillator.stop(now + durationSeconds);
  });
}
